import { useState, useEffect } from "react";
import { FormData } from "../types/GamifiedQuiz.type";
import axios from "axios";
import Cookies from "universal-cookie";

const cookies = new Cookies();

export const useFormData = (
  isV1Api: boolean,
  BACKEND_URL: string,
  entityId: string,
  campaignId?: string,
  questionsPerSection?: number,
  setQuestions?: (count: number) => void,
  GeneralFunctions?: any,
  localeCode?: string | null,
  setQuestThemeData?: Function,
  setBrandTheme?: Function,
  setShowFooter?: Function
) => {
  const [formdata, setFormdata] = useState<FormData[]>([]);
  const [formDataSecondary, setFormDataSecondary] = useState<FormData[]>([]);
  const [campaignVariationId, setCampaignVariationId] = useState("");
  const [totalSectionsPerSectionQuestion, setTotalSectionsPerSectionQuestion] =
    useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getUserFromCookies = () => {
    return {
      externalUserId: cookies.get("externalUserId"),
      questUserId: cookies.get("questUserId"),
      questUserToken: cookies.get("questUserToken"),
    };
  };

  const setUserCookies = (userData: {
    externalUserId: string;
    userId: string;
    token: string;
  }) => {
    const date = new Date();
    date.setHours(date.getHours() + 12);

    cookies.set("externalUserId", userData.externalUserId, {
      path: "/",
      expires: date,
    });
    cookies.set("questUserId", userData.userId, { path: "/", expires: date });
    cookies.set("questUserToken", userData.token, { path: "/", expires: date });
  };

  const getHeaders = async (userId: string, providedHeaders: any) => {
    const cookieData = getUserFromCookies();

    if (cookieData.questUserId && cookieData.questUserToken) {
      return {
        ...providedHeaders,
        userId: cookieData.questUserId,
        token: cookieData.questUserToken,
      };
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}api/users/external/login`,
        {
          externalUserId: userId,
          entityId: entityId,
          ignoreMAU: true,
        },
        { headers: providedHeaders }
      );

      const { userId: newUserId, token } = response.data;

      setUserCookies({
        externalUserId: userId,
        userId: newUserId,
        token,
      });

      return {
        ...providedHeaders,
        userId: newUserId,
        token,
      };
    } catch (error) {
      console.error("Error getting user headers:", error);
      GeneralFunctions.captureSentryException(error);
      throw error;
    }
  };

  const getQuestData = async (
    userId: string,
    headers: object,
    variation?: string
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const enhancedHeaders = await getHeaders(userId, headers);
      const params = new URLSearchParams();
      params.set("platform", "REACT");
      if (variation) params.set("variation", variation);

      const { data } = await axios.get(
        `${BACKEND_URL}api/v2/entities/${entityId}/campaigns/${campaignId}?${params.toString()}`,
        { headers: enhancedHeaders }
      );

      setQuestions?.(data.data.actions.length);

      const actions =
        data?.data?.actions.map((action: any) => ({
          type: action?.actionType,
          question: action?.title,
          options: action?.options || [],
          actionId: action?.actionId,
          required: action?.isRequired,
          placeholder: action?.metadata?.placeholder,
          preventFutureSelection: action?.metadata?.preventFutureSelection,
          preventPastSelection: action?.metadata?.preventPastSelection,
          ...(action?.metadata?.regexKey
            ? { regexKey: action?.metadata?.regexKey }
            : null),
          ...(action?.metadata?.regexErrorMessage
            ? { regexMessage: action?.metadata?.regexErrorMessage }
            : null),
        })) || [];

      if (questionsPerSection > 0) {
        setTotalSectionsPerSectionQuestion(
          Math.ceil(data.data.actions.length / questionsPerSection)
        );
      }
      if (data.data.sdkConfig?.uiProps?.questThemeData) {
        setQuestThemeData(data.data.sdkConfig.uiProps.questThemeData);
      }
      if (data.data.sdkConfig?.uiProps?.BrandTheme?.properties) {
        setBrandTheme(data.data.sdkConfig.uiProps.BrandTheme?.properties);
      }
      if (data?.data?.sdkConfig?.uiProps?.showFooter !== undefined) {
        setShowFooter(data.data.sdkConfig.uiProps.showFooter);
      }
      // console.log("actions", actions);
      setFormdata(actions || []);
      setCampaignVariationId(data.data.campaignVariationId);
    } catch (error) {
      console.error("Error fetching quest data:", error);
      setError("Failed to fetch quest data");
      GeneralFunctions.captureSentryException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getQuestDataV1 = async (
    userId: string,
    headers: object,
    translations: any,
    variation?: string
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const enhancedHeaders = await getHeaders(userId, headers);
      const url = `${BACKEND_URL}api/entities/${entityId}/quests/${campaignId}/criterias?userId=${userId}&getVariation=${
        variation || false
      }`;

      const { data } = await axios.get(url, { headers: enhancedHeaders });

      setQuestions(data?.data?.eligibilityCriterias.length);

      const criterias = data?.data?.eligibilityData.map((criteria: any) => ({
        type: criteria?.criteriaType,
        question: criteria?.metadata?.title,
        options: criteria?.metadata?.options || [],
        criteriaId: criteria?.criteriaId,
        required: criteria?.metadata?.isRequired,
        placeholder: criteria?.metadata?.placeholder,
        keyId: criteria?.metadata?.keyId || "",
        optionsKeyIds: criteria?.metadata?.optionsKeyIds || [],
        preventFutureSelection: criteria?.metadata?.preventFutureSelection,
        preventPastSelection: criteria?.metadata?.preventPastSelection,
      }));

      if (questionsPerSection > 0) {
        setTotalSectionsPerSectionQuestion(
          Math.ceil(
            data?.data?.eligibilityCriterias.length / questionsPerSection
          )
        );
      }

      if (criterias?.length > 0) {
        const [translatedActionsWithoutOptions, translatedCriteriaWithOptions] =
          await Promise.all([
            updateTranslation(criterias, translations, false),
            updateTranslation(criterias, translations, true),
          ]);

        setFormdata(translatedActionsWithoutOptions);
        setFormDataSecondary(translatedCriteriaWithOptions);
      } else {
        setFormdata([]);
      }
    } catch (error) {
      console.error("Error fetching v1 quest data:", error);
      setError("Failed to fetch quest data");
      GeneralFunctions.captureSentryException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTranslation = async (
    criterias: any[],
    translations: any[] | undefined,
    isOption: boolean
  ): Promise<any[]> => {
    try {
      return criterias.map((criteria) => {
        const translation = translations?.find(
          (t) =>
            t.name === localeCode &&
            t.key.includes(criteria.criteriaId) &&
            !t.key.includes("OPTION")
        );

        if (translation) {
          return {
            ...criteria,
            question: translation.message,
            options: isOption
              ? criteria.options.map((option: string) => {
                  const optionTranslation = translations?.find(
                    (t) =>
                      t.name === localeCode &&
                      t.key.includes(option) &&
                      t.key.includes(criteria.criteriaId)
                  );
                  return optionTranslation ? optionTranslation.message : option;
                })
              : criteria?.options,
          };
        }
        return criteria;
      });
    } catch (error) {
      GeneralFunctions.captureSentryException(error);
      console.error("Error updating translations:", error);
      return criterias;
    }
  };

  return {
    formdata,
    formDataSecondary,
    campaignVariationId,
    totalSectionsPerSectionQuestion,
    isLoading,
    error,
    getQuestData,
    getQuestDataV1,
    getUserFromCookies,
    setUserCookies,
  };
};
